import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Users, 
  Search, 
  Trash2, 
  AlertTriangle,
  Shield,
  ShieldCheck,
  Mail,
  Calendar,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MASTER_EMAIL = 'businessgenie.bg@gmail.com';

export default function AdminUserManagement() {
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'tenants'
  
  // Delete flow states
  const [showDeleteStep1, setShowDeleteStep1] = useState(false);
  const [showDeleteStep2, setShowDeleteStep2] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [userConfirmationInput, setUserConfirmationInput] = useState('');
  const [deleteWarning, setDeleteWarning] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Trial edit states
  const [showTrialDialog, setShowTrialDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [trialDays, setTrialDays] = useState('14');
  const [updatingTrial, setUpdatingTrial] = useState(false);

  // Check if current user is the master account
  const isMasterAccount = user?.email?.toLowerCase() === MASTER_EMAIL.toLowerCase();

  useEffect(() => {
    if (isMasterAccount) {
      fetchUsers();
      fetchTenants();
    }
  }, [isMasterAccount]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: searchQuery ? { search: searchQuery } : {},
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to load tenants');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  // Step 1: Prepare deletion - get confirmation code
  const handlePrepareDelete = async (userToDelete) => {
    setSelectedUser(userToDelete);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/users/${userToDelete.id}/prepare-delete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setConfirmationCode(response.data.confirmation_code);
      setDeleteWarning(response.data.warning);
      setShowDeleteStep1(true);
    } catch (error) {
      console.error('Error preparing deletion:', error);
      toast.error(error.response?.data?.detail || 'Failed to prepare deletion');
    }
  };

  // Step 2: Confirm deletion
  const handleConfirmDelete = async () => {
    if (userConfirmationInput !== confirmationCode) {
      toast.error('Confirmation code does not match');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${selectedUser.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { confirmation_code: userConfirmationInput },
        }
      );
      
      toast.success(`User "${selectedUser.email}" has been deleted`);
      setShowDeleteStep2(false);
      setShowDeleteStep1(false);
      setSelectedUser(null);
      setConfirmationCode('');
      setUserConfirmationInput('');
      fetchUsers();
      fetchTenants();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteDialogs = () => {
    setShowDeleteStep1(false);
    setShowDeleteStep2(false);
    setSelectedUser(null);
    setConfirmationCode('');
    setUserConfirmationInput('');
    setDeleteWarning('');
  };

  // Handle trial edit
  const handleOpenTrialEdit = (tenant) => {
    setSelectedTenant(tenant);
    setTrialDays('14'); // Default 14 days
    setShowTrialDialog(true);
  };

  const handleUpdateTrial = async () => {
    const days = parseInt(trialDays, 10);
    if (isNaN(days) || days < 0) {
      toast.error('Please enter a valid number of days');
      return;
    }

    setUpdatingTrial(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/tenants/${selectedTenant.id}`,
        { trial_days: days },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Trial updated to ${days} days for ${selectedTenant.name}`);
      setShowTrialDialog(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (error) {
      console.error('Error updating trial:', error);
      toast.error(error.response?.data?.detail || 'Failed to update trial');
    } finally {
      setUpdatingTrial(false);
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Calculate days remaining
  const getDaysRemaining = (trialEndsAt) => {
    if (!trialEndsAt) return null;
    const now = new Date();
    const end = new Date(trialEndsAt);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Access denied for non-master accounts
  if (!isMasterAccount) {
    return (
      <div className="container mx-auto p-6">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="flex items-center gap-4 p-6">
            <Shield className="h-12 w-12 text-red-500" />
            <div>
              <h2 className="text-xl font-semibold text-red-700">Access Denied</h2>
              <p className="text-red-600">
                Only the Head Admin account ({MASTER_EMAIL}) can access user management.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-8 w-8 text-primary" />
            Admin Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage users and tenants (Head Admin only)
          </p>
        </div>
        <Button onClick={() => { fetchUsers(); fetchTenants(); }} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'users' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('users')}
          data-testid="tab-users-btn"
        >
          <Users className="h-4 w-4 mr-2" />
          Users ({users.length})
        </Button>
        <Button
          variant={activeTab === 'tenants' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('tenants')}
          data-testid="tab-tenants-btn"
        >
          <Building2 className="h-4 w-4 mr-2" />
          Tenants ({tenants.length})
        </Button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Search */}
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="user-search-input"
                  />
                </div>
                <Button type="submit" data-testid="user-search-btn">
                  Search
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Users List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                All Users ({users.length})
              </CardTitle>
              <CardDescription>
                Click the delete button to remove a user account
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No users found
                </div>
              ) : (
                <div className="divide-y">
                  {users.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between py-4 hover:bg-muted/50 px-4 -mx-4 rounded-lg transition-colors"
                      data-testid={`user-row-${u.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold">
                            {u.name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{u.name}</span>
                            {u.is_master && (
                              <Badge variant="default" className="bg-amber-500">
                                <ShieldCheck className="h-3 w-3 mr-1" />
                                Head Admin
                              </Badge>
                            )}
                            {u.is_platform_admin && !u.is_master && (
                              <Badge variant="secondary">
                                <Shield className="h-3 w-3 mr-1" />
                                Admin
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {u.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Status badges */}
                        {u.is_active ? (
                          <Badge variant="outline" className="border-green-500 text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-red-500 text-red-600">
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                        
                        {u.is_email_verified ? (
                          <Badge variant="outline" className="border-blue-500 text-blue-600">
                            Verified
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-500 text-amber-600">
                            Unverified
                          </Badge>
                        )}
                        
                        {/* Delete button - disabled for master account */}
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={u.is_master}
                          onClick={() => handlePrepareDelete(u)}
                          data-testid={`delete-user-btn-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Tenants Tab */}
      {activeTab === 'tenants' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              All Tenants ({tenants.length})
            </CardTitle>
            <CardDescription>
              Manage tenant subscriptions and trial periods
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : tenants.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No tenants found
              </div>
            ) : (
              <div className="divide-y">
                {tenants.map((tenant) => {
                  const daysRemaining = getDaysRemaining(tenant.trial_ends_at);
                  return (
                    <div
                      key={tenant.id}
                      className="flex items-center justify-between py-4 hover:bg-muted/50 px-4 -mx-4 rounded-lg transition-colors"
                      data-testid={`tenant-row-${tenant.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{tenant.name}</span>
                            <Badge variant="secondary" className="capitalize">
                              {tenant.subscription_plan}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {tenant.owner_email || 'No owner'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Created {formatDate(tenant.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Subscription status */}
                        <Badge 
                          variant="outline" 
                          className={
                            tenant.subscription_status === 'active' 
                              ? 'border-green-500 text-green-600' 
                              : tenant.subscription_status === 'trialing'
                              ? 'border-blue-500 text-blue-600'
                              : tenant.subscription_status === 'past_due'
                              ? 'border-red-500 text-red-600'
                              : 'border-gray-500 text-gray-600'
                          }
                        >
                          {tenant.subscription_status}
                        </Badge>
                        
                        {/* Trial info */}
                        {tenant.subscription_status === 'trialing' && daysRemaining !== null && (
                          <Badge 
                            variant="outline" 
                            className={
                              daysRemaining <= 3 
                                ? 'border-amber-500 text-amber-600' 
                                : 'border-cyan-500 text-cyan-600'
                            }
                          >
                            <Clock className="h-3 w-3 mr-1" />
                            {daysRemaining > 0 ? `${daysRemaining} days left` : 'Expired'}
                          </Badge>
                        )}
                        
                        {/* Edit trial button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenTrialEdit(tenant)}
                          data-testid={`edit-trial-btn-${tenant.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit Trial
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Step 1: First Warning Dialog */}
      <AlertDialog open={showDeleteStep1} onOpenChange={setShowDeleteStep1}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete User Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                You are about to delete the following account:
              </p>
              <div className="bg-muted p-4 rounded-lg">
                <p className="font-medium">{selectedUser?.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
              </div>
              <p className="text-red-600 font-medium">
                {deleteWarning}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeDeleteDialogs}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setShowDeleteStep1(false);
                setShowDeleteStep2(true);
              }}
              data-testid="delete-step1-confirm-btn"
            >
              Yes, I want to delete this account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Step 2: Final Confirmation with Code */}
      <Dialog open={showDeleteStep2} onOpenChange={setShowDeleteStep2}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Final Confirmation Required
            </DialogTitle>
            <DialogDescription>
              To confirm deletion, enter the confirmation code below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Confirmation Code:</strong>
              </p>
              <p className="text-2xl font-mono font-bold text-amber-900 mt-1">
                {confirmationCode}
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Enter the confirmation code to proceed:
              </label>
              <Input
                value={userConfirmationInput}
                onChange={(e) => setUserConfirmationInput(e.target.value.toUpperCase())}
                placeholder="Enter code..."
                className="font-mono text-lg tracking-wider"
                maxLength={8}
                data-testid="delete-confirmation-code-input"
              />
            </div>
            
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> Deleting <strong>{selectedUser?.email}</strong> will:
              </p>
              <ul className="text-sm text-red-600 mt-2 list-disc list-inside">
                <li>Permanently remove the user account</li>
                <li>Delete their associated tenant/business data</li>
                <li>Cancel any active subscriptions</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeDeleteDialogs}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting || userConfirmationInput !== confirmationCode}
              data-testid="delete-step2-final-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Permanently Delete Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Trial Dialog */}
      <Dialog open={showTrialDialog} onOpenChange={setShowTrialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              Edit Trial Period
            </DialogTitle>
            <DialogDescription>
              Set the number of trial days for this tenant. The trial will be extended from today.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="font-medium text-blue-900">{selectedTenant?.name}</p>
              <p className="text-sm text-blue-700">{selectedTenant?.owner_email}</p>
              <p className="text-sm text-blue-600 mt-1">
                Current status: <strong className="capitalize">{selectedTenant?.subscription_status}</strong>
              </p>
              {selectedTenant?.trial_ends_at && (
                <p className="text-sm text-blue-600">
                  Trial ends: {formatDate(selectedTenant?.trial_ends_at)}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="trial_days">Trial Days (from today)</Label>
              <Input
                id="trial_days"
                type="number"
                min="0"
                max="365"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                placeholder="14"
                data-testid="trial-days-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter 0 to end the trial immediately. Max 365 days.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrialDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTrial}
              disabled={updatingTrial}
              data-testid="update-trial-btn"
            >
              {updatingTrial ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Update Trial
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
